import { useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, IconButton, Stack } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useMarketingInteriorNavItems } from './useMarketingNavItems';
import MarketingNavDrawerPanel from './MarketingNavDrawerPanel';
import useMarketingNavDrawerItems from './useMarketingNavDrawerItems';
import { NiicoLogoHeader } from '@/components/Logos';
import MarketingNavDesktopItem from './MarketingNavDesktopItem';
import { MarketingNavGalleryPanel } from './MarketingNavGalleryMegaPanel';
import { MarketingNavMegaMenuProvider } from './MarketingNavGalleryContext';
import { MarketingNavNewsMediaPanel } from './MarketingNavNewsMediaMegaPanel';
import { MarketingNavStudioPanel } from './MarketingNavStudioMegaPanel';
import { MarketingNavTradePanel } from './MarketingNavTradeMegaPanel';
import {
  HOME_NAV_BAR_PX,
  HOME_NAV_BAR_PY,
  HOME_NAV_DESKTOP_DISPLAY,
  HOME_NAV_MOBILE_DISPLAY,
  HOME_NAV_STACK_SPACING,
  MARKETING_INTERIOR_NAV_LINK_HOVER_UNDERLINE_COLOR,
} from './home-landing-data';

const LOGO_STYLES = {
  width: 'auto',
  height: '100%',
  maxHeight: 52,
};

const navItemShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  inset: PropTypes.bool,
});

export default function MarketingInteriorNav({ logoHref = '/', items: itemsProp, sx }) {
  const defaultItems = useMarketingInteriorNavItems();
  const items = itemsProp ?? defaultItems;
  const drawerItems = useMarketingNavDrawerItems(items);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <MarketingNavMegaMenuProvider>
      <Box
        component="nav"
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          zIndex: 10,
          flexShrink: 0,
          alignSelf: 'stretch',
          width: '100%',
          maxWidth: 'none',
          margin: 0,
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.08)',
          bgcolor: 'background.default',
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
            width: '100%',
            px: HOME_NAV_BAR_PX,
            py: HOME_NAV_BAR_PY,
          }}
        >
          <Link href={logoHref} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Box
              sx={{
                height: { xs: 40, md: 52 },
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                minWidth: { xs: 140, md: 180 },
              }}
            >
              <NiicoLogoHeader logoContext="interior" styles={LOGO_STYLES} />
            </Box>
          </Link>

          <Box
            sx={{
              display: HOME_NAV_DESKTOP_DISPLAY,
              alignItems: 'center',
              gap: HOME_NAV_STACK_SPACING,
            }}
          >
            <Stack direction="row" spacing={HOME_NAV_STACK_SPACING} sx={{ alignItems: 'stretch' }}>
              {items.map(item => (
                <MarketingNavDesktopItem
                  key={item.label}
                  item={item}
                  underlineColor={MARKETING_INTERIOR_NAV_LINK_HOVER_UNDERLINE_COLOR}
                  color="rgba(0,0,0,0.72)"
                  activeColor="#0a0a0a"
                />
              ))}
            </Stack>
            <IconButton aria-label="Open menu" onClick={openDrawer} sx={{ color: '#0a0a0a' }}>
              <MenuIcon />
            </IconButton>
          </Box>

          <IconButton
            aria-label="Open menu"
            onClick={openDrawer}
            sx={{ display: HOME_NAV_MOBILE_DISPLAY, color: '#0a0a0a' }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <MarketingNavGalleryPanel />
        <MarketingNavNewsMediaPanel />
        <MarketingNavTradePanel />
        <MarketingNavStudioPanel />
        <MarketingNavDrawerPanel
          open={drawerOpen}
          onOpen={openDrawer}
          onClose={closeDrawer}
          items={drawerItems}
        />
      </Box>
    </MarketingNavMegaMenuProvider>
  );
}

MarketingInteriorNav.propTypes = {
  logoHref: PropTypes.string,
  items: PropTypes.arrayOf(navItemShape),
  sx: PropTypes.object,
};
